import { NetworkStatus, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { CloudIntegrationAttachment } from "shared/CloudIntegration/types";

import { GET_ATTACHED_MODULE_INTEGRATIONS } from "../gql";
import { GetAttachedCloudIntegrationsGql } from "../types";

type UseAttachedIntegration = {
  manuallyAttachedIntegrations: CloudIntegrationAttachment[];
  autoAttachedIntegrations: CloudIntegrationAttachment[];
  loading: boolean;
  hasData: boolean;
  refetch: () => void;
};

const useAttachedCloudIntegration = (moduleId: string | undefined): UseAttachedIntegration => {
  const { loading, data, networkStatus, refetch } = useQuery<GetAttachedCloudIntegrationsGql>(
    GET_ATTACHED_MODULE_INTEGRATIONS,
    {
      variables: {
        moduleId,
      },
      skip: !moduleId,
      fetchPolicy: "no-cache",
    }
  );

  const integrations = useMemo(() => {
    const awsIntegrations = data?.module?.integrations?.awsV2 || [];
    const azureIntegrations = data?.module?.integrations?.azure || [];

    return ([] as Array<CloudIntegrationAttachment>)
      .concat(awsIntegrations)
      .concat(azureIntegrations);
  }, [data?.module?.integrations?.awsV2, data?.module?.integrations?.azure]);

  const manuallyAttachedIntegrations = useMemo(() => {
    const manualIntegrations = integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return !integration.isAutoattached;
      }
      return true; // if not autoattached, it's a manually attached integration
    });
    return manualIntegrations;
  }, [integrations]);

  const autoAttachedIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return integration.isAutoattached;
      }
      return false;
    });
  }, [integrations]);

  return {
    manuallyAttachedIntegrations,
    autoAttachedIntegrations,
    loading: loading && networkStatus !== NetworkStatus.refetch,
    refetch,
    hasData: !loading && !!data?.module,
  };
};

export default useAttachedCloudIntegration;
